import TnButton from '../../../../_components/tn-button/TnButton';
import TnTypography from '../../../../_components/tn-typography/TnTypography';
import { createStyles, makeStyles } from '@material-ui/core';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import React from 'react';

import Education from './Education';
import EducationForm, { EducationEntity } from './EducationForm';

interface EducationHandlers {
  onAddEducation: (education: EducationEntity) => void;
  onEditEducation: (index: number, education: EducationEntity) => void;
  onDeleteEducation: (index: number) => void;
}

interface Props {
  educationHistory: EducationEntity[];
  changeHandlers: EducationHandlers;
  onFormOpenChange?: (isOpen: boolean) => void;
  onFormValidityChange?: (isValid: boolean, invalidFields: string[]) => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    labelContainer: {
      width: '100%',
      marginBottom: 16,
    },
    addEducationButton: {
      width: 200,
    },
    filledOutHistoryContainer: {
      width: 'calc(100% - 32px)',
      backgroundColor: '#FAFAFA',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      border: '1px solid #E0E0E0',
      padding: '16px',
      gridGap: 8,
      borderRadius: 4,
      justifyContent: 'flex-start',
      marginBottom: 16,
      '&:not(:last-child)': {
        borderBottom: `1px solid #E0E0E0`,
      },
    },
  }),
);

export default function EducationHistory({
  educationHistory,
  changeHandlers,
  onFormOpenChange,
  onFormValidityChange,
}: Readonly<Props>) {
  const classes = useStyles();

  const [newEducationFormOpen, setNewEducationFormOpen] = React.useState(false);

  const handleFormOpenChange = React.useCallback(
    (isOpen: boolean) => {
      onFormOpenChange?.(isOpen);
    },
    [onFormOpenChange],
  );

  const sortedEducationHistory = educationHistory.sort((a, b) => {
    const aEndDate = a.endDate || new Date().toISOString();
    const bEndDate = b.endDate || new Date().toISOString();

    return new Date(bEndDate).getTime() - new Date(aEndDate).getTime();
  });

  return (
    <>
      <div className={classes.labelContainer} style={{ marginTop: 16 }}>
        <TnTypography bold variant="body">
          Education
        </TnTypography>
      </div>
      <div className={classes.labelContainer}>
        <TnTypography variant="caption">Provide details of your education</TnTypography>
      </div>
      {sortedEducationHistory.length > 0 && (
        <div className={classes.filledOutHistoryContainer}>
          {sortedEducationHistory.map((education, index) => (
            <Education
              key={education.schoolName}
              education={education}
              onFormOpenChange={handleFormOpenChange}
              onFormValidityChange={onFormValidityChange}
              changeHandlers={{
                onEditEducation: (updatedEducation) => changeHandlers.onEditEducation(index, updatedEducation),
                onDeleteEducation: () => changeHandlers.onDeleteEducation(index),
              }}
            />
          ))}
        </div>
      )}
      {newEducationFormOpen && (
        <EducationForm
          mode="create"
          onCancel={() => {
            setNewEducationFormOpen(false);
            handleFormOpenChange(false);
          }}
          onSubmit={(education) => {
            changeHandlers.onAddEducation(education);
            setNewEducationFormOpen(false);
            handleFormOpenChange(false);
          }}
          onValidityChange={onFormValidityChange}
        />
      )}
      <div className={classes.addEducationButton}>
        <TnButton
          startIcon={<AddCircleOutline />}
          value="Add Education"
          variant="outlined"
          color="secondary"
          disabled={newEducationFormOpen}
          onClick={() => {
            setNewEducationFormOpen(true);
            handleFormOpenChange(true);
          }}
        />
      </div>
    </>
  );
}
